
import React, { useState, useEffect, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import Header from './Header';
import Sidebar from './Sidebar';
import ToastContainer from './feature/ToastContainer';
import { useToast } from '../hooks/useToast';
import { NOTIFICATION_EVENT_NAME, type NotificationEvent } from '../services/notificationService';
import { useSidebarStore } from '../stores/sidebarStore';
import { useAuth } from '../context/AuthContext';
import { maudErpMenuItems } from '../modules/business/maud-erp/MaudErpLayoutWrapper';
import { TableSkeleton } from '@/components/base/Skeleton';

interface MenuItemData {
  path: string;
  label: string;
  icon: string;
  subItems?: Array<{
    path: string;
    label: string;
    icon: string;
    tooltip?: string;
  }>;
  tooltip?: string;
}

const Layout: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { user } = useAuth();

  // Use sidebar store for collapsed/pinned state (allows other components to control it)
  const { isCollapsed: isSidebarCollapsed, isPinned: isSidebarPinned, toggleCollapse, togglePin } = useSidebarStore();

  const { toasts, removeToast, addToast } = useToast();

  // Share the MAUD-ERP menu overrides (including the merged 9-item Display Devices list)
  // so that the same shortcuts/sub-menus appear on non-MAUD-ERP routes like /app/app-designer,
  // not the stale full list from the DB menu profile.
  const [dynamicMenuItems] = useState<any>(maudErpMenuItems);

  // Listen for global notifications from non-React code (like authorization service)
  useEffect(() => {
    const handleNotification = (event: Event) => {
      const customEvent = event as CustomEvent<NotificationEvent>;
      if (customEvent.detail) {
        const { type, message, duration } = customEvent.detail;
        addToast({ type, message, duration });
      }
    };

    window.addEventListener(NOTIFICATION_EVENT_NAME, handleNotification as EventListener);

    return () => {
      window.removeEventListener(NOTIFICATION_EVENT_NAME, handleNotification as EventListener);
    };
  }, [addToast]);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  const toggleSidebarCollapse = () => {
    toggleCollapse();
  };

  return (
    /*
     * The shell ground, from the theme rather than from `bg-gray-50`.
     *
     * That Tailwind pair painted a flat cool grey behind the whole app in light
     * mode and slate-900 in dark. Neither has any relationship to the violet rail
     * beside it, so the join between them was the highest-contrast edge on the
     * screen and the shell read as two separate windows — a sidebar, and a
     * different application next to it.
     *
     * --theme-body-bg is now a violet-biased neutral, so the two surfaces are one
     * material at two brightnesses, and the seam stops announcing itself.
     */
    <div className="min-h-screen flex flex-col bg-[#F3EDE4] dark:bg-[#17110A]">
      {/* Full-width top bar; publishes its live height as --app-header-h. */}
      <Header
        onToggleSidebar={toggleSidebar}
        isSidebarOpen={isSidebarOpen}
        isSidebarCollapsed={isSidebarCollapsed}
        onToggleSidebarCollapse={toggleSidebarCollapse}
        isSidebarPinned={isSidebarPinned}
        onToggleSidebarPin={togglePin}
      />

      {/* Sidebar card + content, side by side under the bar. Below lg the sidebar
          is a fixed drawer and takes no room in this row. */}
      <div className="flex flex-1 items-start gap-4 p-3 sm:p-4">
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={closeSidebar}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={toggleSidebarCollapse}
        isPinned={isSidebarPinned}
        menuItems={dynamicMenuItems}
        onToggle={toggleSidebar}
      />

      {/* min-w-0: without it this flex item's automatic minimum width is its content's
          min-width, so any wide child (a many-column table) stretches the whole shell past
          the viewport instead of scrolling inside its own container. */}
      <div className="flex flex-col self-stretch min-h-[calc(100vh-var(--app-header-h,4.25rem)-32px)] flex-1 min-w-0">

        <main
          className="shell-main flex-1 overflow-y-auto overflow-x-hidden"
          data-theme="main"
        >
          <div className="pb-1 sm:pb-2 max-w-full">
            <Suspense fallback={
              <TableSkeleton />
            }>
              {/* Keyed on the working tenant: switching UDA in the header remounts the
                  page content (so screens that fetch in useEffect([]) reload against the
                  new tenant) while this shell — header, sidebar, scroll position of the
                  chrome — stays put. Replaces the old window.location.reload(). */}
              <Outlet key={user?.tenantId || 'no-tenant'} />
            </Suspense>
          </div>
        </main>

        {/* Footer - Sticky at bottom */}
        {/*
          The footer was a slate-800 slab — a third unrelated colour closing the
          shell, and the darkest thing on a light page. It now finishes the same
          gradient the rail starts: violet at the left, where it meets the rail's
          foot, clearing to the page ground on the right.
        */}
        <footer className="mt-auto">
          <div className="px-4 py-2">
            <p className="text-center text-xs text-[#7A6A56] dark:text-[#B3A48F]">
              Developed by <span className="font-medium text-[#2A1D0E] dark:text-[#F3EAD9]">Dreamstep Software Innovations Pvt Ltd</span>
            </p>
          </div>
        </footer>
      </div>
      </div>

      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
};

export default Layout;
